import React, { Component, useMemo } from 'react';
import { HighlightedContent } from '../highlighted-content/highlighted-content';
import { NoteFrame } from './note-frame';
import { PDFFrame } from './pdf-frame';
import { CaseAlert } from '../case-alert/case-alert';
import { commonEnums, documentUtils, spanSelectors } from '@codametrix/ui-common';
import { useSelector } from 'react-redux';
import { AnnotatableDocumentWithDrawerWrapper } from './annotatable-document-with-drawer-wrapper';

const { selectSpanAnnotations } = spanSelectors;
export type NotesProps = {
  notes?: CMxCommonApp.DecoratedNote[];
  noteRefs?: { [id: string]: React.RefObject<any> };
};
type NoteProps = {
  note: CMxCommonApp.DecoratedNote;
};


class Note extends Component<NoteProps> {
  static displayName = 'Note';

  render() {
    const { note } = this.props;
    const { segments = [], spans = [], document_id, filetype } = note;

    const content = (
      <HighlightedContent segments={segments} spans={spans} id={document_id} />
    );

    return (
      <div
        className="report case-note qa-report sensitive-data"
        data-testid="case-note">
        {content}
        <div className="divider-space" />
      </div>
    );
  }
}

function NotePlainText({ note }: NoteProps): JSX.Element {
  return (
    <div
      className="report case-note qa-report sensitive-data"
      data-testid="case-note"
      style={{ whiteSpace: 'pre-line' }}>
      {note.content}
    </div>
  );
}

const EmptyNote = () => {
  return (
    <div className="content-vacant qa-empty-note">
      <h4>No document available</h4>
      <h4>
        Please contact CodaMetrix if you expect to see a document for this case
      </h4>
      <p> support@codametrix.com </p>
    </div>
  );
};
EmptyNote.displayName = 'EmptyNote';

const Notes = (props: NotesProps) => {
  const { notes: notesFromProps = [], noteRefs } = props;
  const notes = [...notesFromProps].sort(
    documentUtils.compareDecoratedNotesForNav
  );
  
  const gradeBenchPanelState = useSelector(
    (state: CMx.ApplicationState) => state.gradeBenchPanel
  );
  
  // Extract spanAnnotationEnabled from project_configuration, default to false
  const spanAnnotationEnabled = useMemo(() => {
    return (
      (gradeBenchPanelState?.qaRule?.data?.project_configuration as any)
        ?.spanAnnotationEnabled ?? false
    );
  }, [gradeBenchPanelState?.qaRule?.data?.project_configuration]);

  const spanAnnotations = useMemo(
    () => gradeBenchPanelState ? selectSpanAnnotations(gradeBenchPanelState) : [],
    [gradeBenchPanelState?.invoices, gradeBenchPanelState?.looseSpans]
  );

  const noteContent = notes.map((note) => {
    const synthKey = `${note?.id}`;
    // {
    //   !note.filename && !note.description;
    // }
    let content;
    let notices: any = false;

    if (note.filetype === 'text/html') {
      content = <NoteFrame key={synthKey} iframeNote={note} />;
    } else if (note.filetype === 'application/pdf') {
      content = <PDFFrame key={synthKey} pdfContent={note} />;
    }
    // CQA conditionals will maintain backwards compatability for existing Note displays
    else if (
      note.filetype === 'text/plain' &&
      note.benchmode === commonEnums.BenchMode.GRADE &&
      !spanAnnotationEnabled
    ) {
      content = <NotePlainText key={synthKey} note={note} />;
    }
    else if (
      note.filetype === 'text/plain' &&
      note.benchmode === commonEnums.BenchMode.GRADE
    ) {

      // Use Redux annotations state filtered by document ID
      const noteAnnotations = spanAnnotations.filter(span => 
        span.documentId === note.document_id
      );

      content = (
        <AnnotatableDocumentWithDrawerWrapper
          text={note.content}
          noteId={note.document_id}
          spans={noteAnnotations}
        />
      );
    } else {

      content = <Note key={synthKey} note={note} />;
    }

    const documentNotices = note?.documentNotices ?? [];
    if (documentNotices.length) {
      notices = documentNotices.map((notice, idx) => (
        <CaseAlert
          alertType={notice.level}
          alertSize="sm"
          key={`document-notice-${idx}`}>
          {notice.message}
        </CaseAlert>
      ));
    }

    return (
      <div
        key={note.document_id}
        ref={noteRefs ? noteRefs[note.document_id] : undefined}
        className={`notes-inner ${note.document_id}`}>
        {notices}
        {content}
        {!note.filename && !note.description ? (
          <></>
        ) : (
          <div className="notes-heading">
            <p className="note-text">
              <span className="note-name"> End of {note.filename}</span>
              <span className="note-description">
                {!note.description ? (
                  <></>
                ) : (
                  <>
                    {' — '}
                    {note.description}
                  </>
                )}
              </span>
            </p>
            <hr className="notes-divider" />
          </div>
        )}
      </div>
    );
  });

  return <>{noteContent?.length ? noteContent : <EmptyNote />}</>;
};

Notes.displayName = 'Notes';

export { Notes, EmptyNote };
